import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UserService } from 'app/core/services/user.service';
import { environment } from 'environments/environment';
import { AuthService } from 'app/core/auth/auth.service';
import iframeResize from '@iframe-resizer/parent';
import { ZoomLevelService } from 'app/core/services/zoom-level.service';
import { EventsService } from 'app/core/services/events.service';
import { AutoLogoutService } from 'app/core/services/auto-logout.service';
import { StaffPlanService } from '../../events/pages/staff-plan/staff-plan.service';

@UntilDestroy()
@Component({
    selector: 'app-old-system-iframe',
    templateUrl: './old-system-iframe.component.html',
    styleUrls: ['./old-system-iframe.component.scss'],
    imports: [MatProgressSpinnerModule, CommonModule],
})
export class OldSystemIframeComponent implements AfterViewInit, OnInit {
    iframeLoading: boolean = true;
    internalIframeLoading: boolean = false;
    @Input() src: string;
    @Input() link: string = '';
    @Input() key: string = '';
    iFrameSrc: SafeResourceUrl;

    @ViewChild('myIframe') public myIframe;

    private functionTriggered: boolean = false;
    private _hidden: boolean = true;
    @Input()
    set hidden(value: boolean) {
        this._hidden = value;

        if (!this._hidden && !this.functionTriggered) {
            this.loadIframe();
            this.functionTriggered = true;
        }
    }

    @Output() show: EventEmitter<boolean> = new EventEmitter<boolean>();

    smallZoom = false;

    // @HostListener('window:resize', ['$event'])
    // onResize(event) {
    //     this.zoomLevelService.changeZoom();
    //     this.setZoomLevel();
    // }

    get hidden(): boolean {
        return this._hidden;
    }

    constructor(
        private sanitizer: DomSanitizer,
        private route: ActivatedRoute,
        private userService: UserService,
        private _authService: AuthService,
        private _router: Router,
        public zoomLevelService: ZoomLevelService,
        private events: EventsService,
        private cdr: ChangeDetectorRef,
        private autoLogoutService: AutoLogoutService,
        private staffplanService: StaffPlanService,
    ) {}

    ngOnInit(): void {
        this.setZoomLevel();
        this.events.subscribe('cdr', () => {
            this.cdr.detectChanges();
            this.applyZoomToIframeContent();
        });
    }

    /**
     * Applies the current zoom level to the iframe's BODY element (same-origin only).
     *
     * Previously the zoom was applied to the Angular host element via
     * `[ngStyle]="{ zoom: zoomLevelService.zoomLevel }"`. On mobile (<640px) the
     * service auto-sets zoom to 50%, which visually shrank the host AND the
     * iframe inside it to half-width. By scoping zoom to the iframe's internal
     * document, the iframe element itself fills the viewport while content
     * stays readable at the chosen zoom level.
     */
    private applyZoomToIframeContent(): void {
        if (!this.smallZoom || !this.myIframe?.nativeElement) {
            return;
        }
        try {
            const iframeEl = this.myIframe.nativeElement as HTMLIFrameElement;
            const doc =
                iframeEl.contentDocument ||
                iframeEl.contentWindow?.document;
            if (!doc?.body) {
                return;
            }
            doc.body.style.zoom = this.zoomLevelService.zoomLevel;
        } catch {
            // Cross-origin access denied – PHP system is served same-origin,
            // so this branch only triggers in environments that break that
            // assumption.
        }
    }

    setZoomLevel() {
        if (
            this.src === 'event/Staff_plan' ||
            this.src === 'event_scheduling/Event_scheduling' ||
            this.src === 'event/Video_screen_overview'
        ) {
            this.smallZoom = true;
        } else {
            this.smallZoom = false;
        }
        this.cdr.detectChanges();
    }

    async ngAfterViewInit() {
        this.route.queryParams.pipe(untilDestroyed(this)).subscribe(data => {
            if (data.oldSystem) {
                setTimeout(() => {
                    // this.show.emit(true);
                }, 0);
            }
        });
    }

    async loadIframe() {
        let url: string;
        url = `navigator.php?page=pages/${this.src}.php&uid=${this.userService.user.uid}&screen=full`;
        if (this.link != '') {
            url += `&link=${this.link}`;
        }
        if (this.key != '') {
            url += `&key=${this.key}`;
        }

        this.iFrameSrc = this.sanitizer.bypassSecurityTrustResourceUrl(
            environment.apiURL + url
        );
    }

    iFrameLoaded(myIframe) {
        iframeResize(
            {
                license: 'GPLv3',
                checkOrigin: false,
            },
            myIframe
        );
        this.injectJavaScript(myIframe);
        this.iframeLoading = false;
    }

    injectJavaScript(myIframe) {
        const iframe = myIframe;
        const iframeWindow = iframe.contentWindow;
        const iframeDocument = iframeWindow.document;

        iframe.contentWindow.onbeforeunload = () => {
            console.log('onbeforeunload');
            this.internalIframeLoading = true;
            this.cdr.detectChanges();
        };

        myIframe.addEventListener('unload', () => {
            console.log('unload');
            this.internalIframeLoading = true;
            this.cdr.detectChanges();
        });

        myIframe.addEventListener('load', () => {
            console.log('load');
            this.internalIframeLoading = false;
            this.cdr.detectChanges();

            try {
                this.injectIframeStyles(iframeWindow.document);
            } catch {
                // Cross-origin navigation – iframe-resizer postMessage
                // channel remains the source of truth.
            }
            this.applyZoomToIframeContent();

            const checkUrl = () => {
                if (
                    iframeWindow.location.href.includes(
                        'navigator.php?page=pages/common/Login.php'
                    )
                ) {
                    this.signOut();
                }
            };

            checkUrl();

            const observer = new MutationObserver(checkUrl);
            observer.observe(iframeDocument.body, {
                childList: true,
                subtree: true,
            });
        });

        this.injectIframeStyles(iframeDocument);
        this.applyZoomToIframeContent();
    }

    /**
     * Injects CSS overrides into the iframe document so @iframe-resizer can
     * accurately measure content height.
     *
     * The legacy PHP shell ships `.page-base-right { height: calc(100vh - 90px);
     * overflow-y: scroll }`, which creates an internal scrollable container.
     * @iframe-resizer's ResizeObserver then reports only the container height,
     * not the underlying content - the iframe stays pinned to ~100vh-90px and
     * iOS Safari falls back to its broken internal-iframe scrolling. By
     * neutralizing the fixed height and internal overflow, content flows
     * naturally and the resizer sizes the iframe to real content height, so
     * the outer page handles scrolling (which iOS Safari does well).
     *
     * Marked with `data-angular-inject` so callers can detect prior injection
     * and avoid duplicating <style> nodes when the iframe navigates internally.
     */
    private injectIframeStyles(iframeDocument: Document): void {
        if (
            !iframeDocument?.head ||
            iframeDocument.querySelector('style[data-angular-inject]')
        ) {
            return;
        }
        const style = iframeDocument.createElement('style');
        style.setAttribute('data-angular-inject', 'true');
        style.textContent = `
            .page-top, .page-base-left {
                display: none !important;
            }
            .tab_box {
                height: max-content !important;
            }
            .page-base-right {
                height: auto !important;
                overflow: visible !important;
                overflow-y: visible !important;
                margin-left: 0 !important;
            }
        `;
        iframeDocument.head.appendChild(style);
    }

    signOut(): void {
        this._authService.signOut().subscribe();
        this.autoLogoutService.clearTimer();
        this.staffplanService.resetFilter();
        this._router.navigate(['/sign-in']);
    }

    initializeIframeEventListener(): void {
        window.addEventListener('message', event => {
            if (event.data && event.data.eventType === 'logout') {
                this.signOut();
            } else if (event.data && event.data.eventType === 'loading') {
                this.internalIframeLoading = true;
            } else if (event.data && event.data.eventType === 'loaded') {
                this.internalIframeLoading = false;
            }
        });
    }
}
